import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ToastAction, ToastItem, ToastProps, ToastState, ToastVariant } from './types';

// ─── Variant class maps ───────────────────────────────────────────────────────

const VARIANT_CLASSES: Record<
  ToastVariant,
  { border: string; icon: string; title: string; description: string; progress: string }
> = {
  success: {
    border:      'border-l-success-500',
    icon:        'text-success-500',
    title:       'text-neutral-900 dark:text-neutral-100',
    description: 'text-neutral-600 dark:text-neutral-400',
    progress:    'bg-success-500',
  },
  error: {
    border:      'border-l-danger-500',
    icon:        'text-danger-500',
    title:       'text-neutral-900 dark:text-neutral-100',
    description: 'text-neutral-600 dark:text-neutral-400',
    progress:    'bg-danger-500',
  },
  warning: {
    border:      'border-l-warning-500',
    icon:        'text-warning-500',
    title:       'text-neutral-900 dark:text-neutral-100',
    description: 'text-neutral-600 dark:text-neutral-400',
    progress:    'bg-warning-500',
  },
  info: {
    border:      'border-l-brand-500',
    icon:        'text-brand-500',
    title:       'text-neutral-900 dark:text-neutral-100',
    description: 'text-neutral-600 dark:text-neutral-400',
    progress:    'bg-brand-500',
  },
};

// ─── ARIA attributes per variant ──────────────────────────────────────────────

const ARIA_ATTRS: Record<ToastVariant, { role: 'status' | 'alert'; ariaLive: 'polite' | 'assertive' }> = {
  info:    { role: 'status', ariaLive: 'polite' },
  success: { role: 'status', ariaLive: 'polite' },
  warning: { role: 'alert',  ariaLive: 'assertive' },
  error:   { role: 'alert',  ariaLive: 'assertive' },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckCircleIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
      />
    </svg>
  );
}

function XCircleIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z"
      />
    </svg>
  );
}

function AlertTriangleIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253l-.37 1.615A1.75 1.75 0 0 0 10.592 14H11a.75.75 0 0 0 0-1.5h-.408l.37-1.615A1.75 1.75 0 0 0 9.408 9H9Z"
      />
    </svg>
  );
}

const DEFAULT_ICONS: Record<ToastVariant, (cls: string) => React.ReactElement> = {
  success: (cls) => <CheckCircleIcon className={cls} />,
  error:   (cls) => <XCircleIcon className={cls} />,
  warning: (cls) => <AlertTriangleIcon className={cls} />,
  info:    (cls) => <InfoIcon className={cls} />,
};

// ─── XIcon (dismiss button) ───────────────────────────────────────────────────

function XIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

// ─── Single Toast item ────────────────────────────────────────────────────────

const ENTER_DURATION = 200;
const EXIT_DURATION  = 150;
const DEFAULT_DURATION = 5000;

interface SingleToastProps extends ToastProps {
  onDismiss: () => void;
}

function SingleToast({
  variant = 'info',
  title,
  description,
  duration = DEFAULT_DURATION,
  onDismiss,
  action,
}: SingleToastProps) {
  const [toastState, setToastState] = useState<ToastState>('entering');
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pausedAtRef     = useRef<number | null>(null);
  const remainingRef    = useRef<number>(duration);

  const c = VARIANT_CLASSES[variant];
  const { role, ariaLive } = ARIA_ATTRS[variant];

  // Enter → visible
  useEffect(() => {
    const t = setTimeout(() => setToastState('visible'), ENTER_DURATION);
    return () => clearTimeout(t);
  }, []);

  // Schedule auto-dismiss
  const scheduleDismiss = useCallback(() => {
    if (remainingRef.current === 0) return;
    dismissTimerRef.current = setTimeout(() => {
      setToastState('exiting');
      setTimeout(onDismiss, EXIT_DURATION);
    }, remainingRef.current);
  }, [onDismiss]);

  const cancelDismiss = useCallback(() => {
    if (dismissTimerRef.current !== null) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (toastState === 'visible' && duration > 0) {
      pausedAtRef.current = Date.now();
      scheduleDismiss();
    }
    return cancelDismiss;
  }, [toastState, duration, scheduleDismiss, cancelDismiss]);

  function handleMouseEnter() {
    if (duration === 0) return;
    cancelDismiss();
    if (pausedAtRef.current !== null) {
      remainingRef.current -= Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }
  }

  function handleMouseLeave() {
    if (duration === 0) return;
    pausedAtRef.current = Date.now();
    scheduleDismiss();
  }

  function handleDismiss() {
    cancelDismiss();
    setToastState('exiting');
    setTimeout(onDismiss, EXIT_DURATION);
  }

  // Transition classes
  const transitionClasses =
    toastState === 'entering'
      ? 'opacity-0 -translate-y-2'
      : toastState === 'exiting'
        ? 'opacity-0'
        : 'opacity-100 translate-y-0';

  const transitionDuration =
    toastState === 'entering'
      ? 'duration-200'
      : toastState === 'exiting'
        ? 'duration-150'
        : 'duration-200';

  return (
    <div
      role={role}
      aria-live={ariaLive}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={[
        'relative flex w-full flex-col overflow-hidden rounded-lg border border-l-4 bg-white shadow-md',
        'dark:bg-neutral-800 dark:border-neutral-700',
        c.border,
        'transition-all',
        transitionDuration,
        transitionClasses,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Content row */}
      <div className="flex items-start gap-3 p-3 sm:p-4">
        {/* Icon */}
        <span className="mt-0.5 shrink-0">
          {DEFAULT_ICONS[variant](`h-5 w-5 ${c.icon}`)}
        </span>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-5 ${c.title}`}>{title}</p>
          {description && (
            <p className={`mt-0.5 text-sm leading-5 ${c.description}`}>{description}</p>
          )}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className={[
                'mt-2 text-sm font-semibold underline-offset-2 hover:underline rounded',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1',
                c.icon,
              ].join(' ')}
            >
              {action.label}
            </button>
          )}
        </div>

        {/* Dismiss button */}
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          className={[
            'shrink-0 rounded p-0.5 text-neutral-400 transition-colors',
            'hover:text-neutral-600 dark:hover:text-neutral-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1',
          ].join(' ')}
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar — only when timed */}
      {duration > 0 && toastState === 'visible' && (
        <div className="h-0.5 w-full overflow-hidden bg-neutral-100 dark:bg-neutral-700">
          <div
            className={`h-full ${c.progress} origin-left`}
            style={{
              animation: `toast-progress ${remainingRef.current}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── ToastContext ─────────────────────────────────────────────────────────────

interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (props: ToastProps) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ─── ToastProvider ────────────────────────────────────────────────────────────

const MAX_VISIBLE = 3;
let _counter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((props: ToastProps): string => {
    const id = `toast-${++_counter}`;
    setToasts((prev) => {
      const next = [...prev, { ...props, id }];
      // Keep only the last MAX_VISIBLE toasts
      return next.slice(-MAX_VISIBLE);
    });
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

// ─── useToast ─────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');

  // Memoize on the stable underlying callbacks so consumers can safely include
  // `toast`/`dismiss` in effect deps without causing re-runs on every render.
  const { addToast, removeToast } = ctx;
  return useMemo(
    () => ({
      toast: (props: ToastProps) => addToast(props),
      dismiss: (id: string) => removeToast(id),
    }),
    [addToast, removeToast],
  );
}

// ─── ToastContainer ───────────────────────────────────────────────────────────

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <>
      {/* Progress bar keyframe — injected once */}
      <style>{`
        @keyframes toast-progress {
          from { transform: scaleX(1); }
          to   { transform: scaleX(0); }
        }
      `}</style>

      {/* Fixed container: top-right on desktop, full-width at top on mobile */}
      <div
        aria-label="Notifications"
        className={[
          'pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col gap-2 p-3',
          'sm:inset-x-auto sm:right-4 sm:top-4 sm:w-[360px]',
        ].join(' ')}
      >
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto w-full">
            <SingleToast
              variant={t.variant}
              title={t.title}
              description={t.description}
              duration={t.duration}
              action={t.action}
              onDismiss={() => onDismiss(t.id)}
            />
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Toast (standalone, unmanaged) ───────────────────────────────────────────
// For use without ToastProvider — consumer controls mounting/unmounting.

export function Toast(props: SingleToastProps) {
  return <SingleToast {...props} />;
}
